import React from "react";
import { AlertTriangle } from "lucide-react";

import { Callout } from "@/components/ui/Callout";
import { plural } from "@/utils/formatText";

/**
 * What stops working on a database moved down to `entry`'s version.
 *
 * Two facts, and they answer different questions. `denyAllTables` is derived by
 * the server from the archived policy sets: every table a later version polices
 * and this one does not ends up with row security on and no policy at all —
 * deny-everything for the app role. `rollbackNote` is written by hand and says
 * what that means for a person, plus any extra step the rollback needs.
 *
 * The silence is why this is a warning and not a footnote: a SELECT that matches
 * no policy returns zero rows without raising. The feature does not break
 * loudly, it goes quiet — so nobody finds out from an error, they find out from
 * a customer.
 *
 * Rendered by the version drawer, the per-database rollback confirmation and the
 * fleet-wide one, so all three say the same thing about the same version.
 *
 * `inline` exists because the per-database confirmation puts this inside Radix's
 * `AlertDialogDescription`, which is a `<p>`: a `<div>` there is invalid nesting.
 * The wording is built once above the split, so the two renderings cannot drift.
 */
const RollbackHazard = ({ entry, inline = false }) => {
    const denyAll = entry?.denyAllTables ?? [];
    const note = entry?.rollbackNote;
    if (!entry || (!denyAll.length && !note)) return null;

    const heading = `What v${entry.version} leaves behind`;
    const denyAllSentence = denyAll.length
        ? `${plural(denyAll.length, "table", "tables")} ${
              denyAll.length === 1 ? "ends" : "end"
          } up with row security on and no policy — no read, no write, and reads ` +
          `fail silently by returning nothing:`
        : null;

    if (inline) {
        return (
            <span className="block mt-2 text-tone-amber">
                <span className="block font-medium">{heading}</span>
                {note && <span className="block mt-0.5">{note}</span>}
                {denyAllSentence && (
                    <>
                        <span className="block mt-0.5">{denyAllSentence}</span>
                        <span className="block font-mono break-words">
                            {denyAll.join(", ")}
                        </span>
                    </>
                )}
            </span>
        );
    }

    return (
        <Callout tone="warning" icon={AlertTriangle} size="xs">
            <span className="font-medium block mb-0.5">{heading}</span>
            {note && <div className="mb-1">{note}</div>}
            {denyAllSentence && (
                <div>
                    {denyAllSentence}
                    <div className="mt-1 font-mono break-words">
                        {denyAll.join(", ")}
                    </div>
                </div>
            )}
        </Callout>
    );
};

export default RollbackHazard;
